import { activeAccountId, setActiveAccountScope } from '../persistence/accountScope';
import { resolveAccountDatabase } from '../persistence/v2Database';
import { applySignedOutState, clearAccountStores, hydrateAll } from './hydrate';
import { releaseAllMediaUrls } from './mediaUrls';
import { adoptLegacyInspectorProfile } from './preferences';
import { forgetPushRegistration } from './pushRegistration';
import { resetAccountStore } from './accountStore';
import { resetReportCloudStore } from './reportCloudStore';
import { resetDraftRetentionForTests } from './draftRetention';
import { resetAgreementPdfStore } from './agreementPdfStore';

// ENTERING AND LEAVING AN ACCOUNT (account isolation, Owner device test 2026-09-21).
//
// The session store decides WHO is signed in; this module makes the device's data follow:
//   enter  the account's own database is resolved and becomes the only one the persistence layer
//          opens, whatever the previous account left in memory is dropped, the account's records are
//          hydrated — and only then does the session turn "signed in", so no screen ever renders the
//          new account over the old account's stores;
//   leave  the scope is closed FIRST (a late write is refused from that instant), then memory is
//          emptied and the stores read "hydrated, nothing" so the shell shows Sign in.
// The session store calls these through hooks (registerAccountHooks, wired in src/main.tsx) because
// the stores import the session store: a direct import the other way would be a cycle.
let entered: string | null = null;
let queue: Promise<void> = Promise.resolve();

function dropAccountMemory(): void {
  clearAccountStores();
  releaseAllMediaUrls();
  forgetPushRegistration();
  resetAccountStore();
  resetReportCloudStore();
  resetDraftRetentionForTests();
  resetAgreementPdfStore();
}

function serial(work: () => Promise<void>): Promise<void> {
  const next = queue.then(work, work);
  queue = next.catch(() => undefined);
  return next;
}

export function enterAccount(userId: string): Promise<void> {
  return serial(async () => {
    // The same account again (a session refresh, a second initSession): its live stores are the truth,
    // never re-read over work in progress.
    if (entered === userId && activeAccountId() !== null) return;
    entered = null;
    setActiveAccountScope(null, null);
    dropAccountMemory();
    const database = await resolveAccountDatabase(userId);
    setActiveAccountScope(userId, database);
    adoptLegacyInspectorProfile();
    await hydrateAll();
    entered = userId;
  });
}

export function leaveAccount(): Promise<void> {
  return serial(async () => {
    entered = null;
    setActiveAccountScope(null, null);
    dropAccountMemory();
    applySignedOutState();
  });
}

export function enteredAccountId(): string | null {
  return entered;
}

export function resetAccountLifecycleForTests(): void {
  entered = null;
  queue = Promise.resolve();
}
